import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';

const CATEGORIES = ['General', 'Food & Dining', 'Health & Wellness', 'Education', 'Finance', 'Legal', 'Travel', 'Retail', 'Real Estate', 'Technology', 'Other'];

const EMPTY = {
  name: '', category: 'General', description: '', discount: '',
  address: '', suburb: '', state: 'VIC', postcode: '',
  phone: '', email: '', website: '', logo: '', sort_order: 0, active: true,
};

function PartnerForm({ values, onChange, onLogoPickerOpen }) {
  const f = k => ({ value: values[k] ?? '', onChange: e => onChange(k, e.target.value) });
  return (
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label small fw-bold">Business Name *</label>
        <input className="form-control" placeholder="e.g. Sharma Legal Services" {...f('name')} required />
      </div>
      <div className="col-md-6">
        <label className="form-label small fw-bold">Category</label>
        <select className="form-select" value={values.category || 'General'} onChange={e => onChange('category', e.target.value)}>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label small fw-bold">Discount / Offer</label>
        <input className="form-control" placeholder="e.g. 10% off for MNCW members" {...f('discount')} />
      </div>
      <div className="col-12">
        <label className="form-label small fw-bold">Description</label>
        <textarea className="form-control" rows={2} placeholder="Short description of the business…" value={values.description || ''} onChange={e => onChange('description', e.target.value)} style={{ resize: 'vertical' }} />
      </div>

      <div className="col-12"><hr className="my-1" /><div className="small fw-bold text-muted mb-1">Address</div></div>
      <div className="col-12">
        <label className="form-label small fw-bold">Street Address</label>
        <input className="form-control" placeholder="123 Example St" {...f('address')} />
      </div>
      <div className="col-md-5">
        <label className="form-label small fw-bold">Suburb</label>
        <input className="form-control" placeholder="Melton" {...f('suburb')} />
      </div>
      <div className="col-md-3">
        <label className="form-label small fw-bold">State</label>
        <input className="form-control" {...f('state')} />
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-bold">Postcode</label>
        <input className="form-control" placeholder="3337" {...f('postcode')} />
      </div>

      <div className="col-12"><hr className="my-1" /><div className="small fw-bold text-muted mb-1">Contact</div></div>
      <div className="col-md-4">
        <label className="form-label small fw-bold">Phone</label>
        <input className="form-control" placeholder="03 xxxx xxxx" {...f('phone')} />
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-bold">Email</label>
        <input type="email" className="form-control" placeholder="contact@business.com" {...f('email')} />
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-bold">Website</label>
        <input className="form-control" placeholder="https://…" {...f('website')} />
      </div>

      <div className="col-12"><hr className="my-1" /><div className="small fw-bold text-muted mb-1">Logo & Display</div></div>
      <div className="col-12">
        <label className="form-label small fw-bold">Logo</label>
        <div className="d-flex gap-3 align-items-center">
          {values.logo ? (
            <img src={values.logo} alt="logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', padding: 4 }} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-building text-muted opacity-25"></i>
            </div>
          )}
          <div className="flex-grow-1">
            <input className="form-control mb-2" placeholder="Logo URL or pick from media" {...f('logo')} />
            <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={onLogoPickerOpen}>
              <i className="fas fa-folder-open me-1"></i>Browse Media
            </button>
          </div>
        </div>
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-bold">Sort Order</label>
        <input type="number" className="form-control" value={values.sort_order ?? 0} onChange={e => onChange('sort_order', Number(e.target.value))} />
      </div>
      <div className="col-md-4 d-flex align-items-end pb-1">
        <div className="form-check form-switch ms-1">
          <input className="form-check-input" type="checkbox" id="partnerActive" checked={values.active !== false} onChange={e => onChange('active', e.target.checked)} />
          <label className="form-check-label small fw-bold" htmlFor="partnerActive">Active (visible publicly)</label>
        </div>
      </div>
    </div>
  );
}

export default function PartnersManager() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editTarget, setEditTarget] = useState(null); // null | 'new' | partner obj
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    // Load all (including inactive) for admin — use the admin GET
    api.get('/partners').then(r => setPartners(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditTarget('new'); setForm({ ...EMPTY, sort_order: partners.length }); setFormError(''); };
  const openEdit = (p) => { setEditTarget(p); setForm({ ...p }); setFormError(''); };
  const closeEdit = () => { setEditTarget(null); setFormError(''); };

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { setFormError('Business name is required.'); return; }
    setSaving(true); setFormError('');
    try {
      if (editTarget === 'new') {
        await api.post('/partners', form);
      } else {
        await api.put(`/partners/${editTarget.id}`, form);
      }
      closeEdit();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/partners/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } finally { setDeleting(false); }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0">Business Partners</h4>
          <p className="text-muted small mb-0">Businesses offering discounts to MNCW members</p>
        </div>
        <button className="btn btn-primary rounded-pill px-4" onClick={openAdd}>
          <i className="fas fa-plus me-2"></i>Add Partner
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border spinner-border-sm"></span></div>
      ) : partners.length === 0 ? (
        <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
          <i className="fas fa-handshake fa-3x mb-3 d-block opacity-25"></i>
          <div className="mb-3">No business partners yet.</div>
          <button className="btn btn-primary rounded-pill px-5" onClick={openAdd}>
            <i className="fas fa-plus me-2"></i>Add Your First Partner
          </button>
        </div>
      ) : (
        <div className="bg-white rounded shadow-sm overflow-hidden">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th className="small text-muted fw-semibold">Business</th>
                <th className="small text-muted fw-semibold">Category</th>
                <th className="small text-muted fw-semibold">Offer</th>
                <th className="small text-muted fw-semibold">Contact</th>
                <th className="small text-muted fw-semibold">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {p.logo ? (
                        <img src={p.logo} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb' }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fas fa-building text-muted" style={{ fontSize: '0.8rem' }}></i>
                        </div>
                      )}
                      <div>
                        <div className="fw-semibold small">{p.name}</div>
                        {(p.suburb || p.state) && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{[p.suburb, p.state].filter(Boolean).join(', ')}</div>}
                      </div>
                    </div>
                  </td>
                  <td><span className="badge bg-light text-secondary">{p.category}</span></td>
                  <td className="small" style={{ maxWidth: 200 }}>
                    {p.discount ? <span className="text-success fw-semibold">{p.discount}</span> : <span className="text-muted">—</span>}
                  </td>
                  <td className="small text-muted">
                    {p.phone && <div><i className="fas fa-phone me-1"></i>{p.phone}</div>}
                    {p.email && <div><i className="fas fa-envelope me-1"></i>{p.email}</div>}
                  </td>
                  <td>
                    <span className={`badge ${p.active !== false ? 'badge-soft-success' : 'badge-soft-secondary'}`}>
                      {p.active !== false ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="text-nowrap">
                    <button className="btn btn-sm btn-outline-primary rounded-pill me-1" onClick={() => openEdit(p)}><i className="fas fa-pencil-alt"></i></button>
                    <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => setDeleteTarget(p)}><i className="fas fa-trash"></i></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {editTarget !== null && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060, overflowY: 'auto' }} onClick={closeEdit}>
          <div className="modal-dialog modal-xl my-4" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={save}>
                <div className="modal-header">
                  <h5 className="modal-title">{editTarget === 'new' ? 'Add Business Partner' : `Edit — ${editTarget.name}`}</h5>
                  <div className="d-flex align-items-center gap-2 ms-auto me-2">
                    <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-4">
                      {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : <><i className="fas fa-save me-2"></i>{editTarget === 'new' ? 'Add Partner' : 'Save Changes'}</>}
                    </button>
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-3" onClick={closeEdit}>Cancel</button>
                  </div>
                  <button type="button" className="btn-close ms-2" onClick={closeEdit}></button>
                </div>
                <div className="modal-body">
                  {formError && <div className="alert alert-danger py-2 mb-3">{formError}</div>}
                  <PartnerForm values={form} onChange={onChange} onLogoPickerOpen={() => setShowPicker(true)} />
                </div>
                <div className="modal-footer">
                  <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">
                    {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : <><i className="fas fa-save me-2"></i>{editTarget === 'new' ? 'Add Partner' : 'Save Changes'}</>}
                  </button>
                  <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={closeEdit}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Logo image picker */}
      {showPicker && (
        <S3ImagePicker
          onSelect={url => { onChange('logo', url); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.55)', zIndex: 1070 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title text-danger"><i className="fas fa-exclamation-triangle me-2"></i>Remove Partner?</h5>
              </div>
              <div className="modal-body pt-2">
                <p>Remove <strong>{deleteTarget.name}</strong> from the partners list? This cannot be undone.</p>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
                <button className="btn btn-danger rounded-pill px-4" onClick={confirmDelete} disabled={deleting}>
                  {deleting ? <><span className="spinner-border spinner-border-sm me-2"></span>Removing…</> : 'Yes, Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
